'use strict';
(function () {
  if (!customElements.get('marquee-text')) {
    /* jld_1004 speed: marquee is now a pure CSS animation (compositor thread) instead of a GSAP
       timeline driven by JavaScript on every frame. gsap.min.js is no longer loaded by the theme.
       Visible behaviour kept: continuous scroll at the configured speed/direction, pause while
       hovered + random hover colours, short periodic pause (every 7 s for pause-duration ms).
       Setup still waits until the banner is near the viewport (one layout read, no per-frame work). */
    class MarqueeText extends HTMLElement {
      constructor() {
        super();
        this.started = false;
        this.isHovered = false;
        this.isOnScreen = false;
        this.pauseInterval = null;
        this.pauseTimeout = null;
        this.pauseDuration = parseInt(this.getAttribute("pause-duration")) || 2000;
        this.scrollSpeed = this.getScrollSpeed();
        this.availableColors = [];
        this.previousColorIndex = -1;
        this.applyRandomColors = this.applyRandomColors.bind(this);
        this.onEnter = this.onEnter.bind(this);
        this.onLeave = this.onLeave.bind(this);
        this.onResize = this.onResize.bind(this);
      }

      getScrollSpeed() {
        const speed = this.getAttribute("data-speed") || "fast";
        const speedMap = { "slow": 50, "normal": 100, "fast": 200, "very-fast": 400, "ultra-fast": 700 };
        return speedMap[speed] || 200;
      }

      connectedCallback() {
        this.loadAvailableColors();
        this.addEventListener("mouseenter", this.applyRandomColors);
        if (!('IntersectionObserver' in window)) { this.start(); return; }
        this.visObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => { this.isOnScreen = entry.isIntersecting; });
          this.syncState();
        }, { root: null, rootMargin: '200px 0px', threshold: 0 });
        this.visObserver.observe(this);
        this.onVisibilityChange = () => this.syncState();
        document.addEventListener('visibilitychange', this.onVisibilityChange);
      }

      disconnectedCallback() {
        this.removeEventListener("mouseenter", this.applyRandomColors);
        if (this.visObserver) this.visObserver.disconnect();
        if (this.onVisibilityChange) document.removeEventListener('visibilitychange', this.onVisibilityChange);
        if (this.pauseInterval) clearInterval(this.pauseInterval);
        if (this.pauseTimeout) clearTimeout(this.pauseTimeout);
        window.removeEventListener('resize', this.onResize);
        if (this.parentElement) {
          this.parentElement.removeEventListener("mouseenter", this.onEnter);
          this.parentElement.removeEventListener("mouseleave", this.onLeave);
        }
      }

      loadAvailableColors() {
        const maxPairs = 4;
        for (let i = 1; i <= maxPairs; i++) {
          const bgColor = this.getAttribute(`data-bg-${i}`);
          const textColor = this.getAttribute(`data-text-${i}`);
          if (bgColor && textColor) this.availableColors.push({ bg: `${bgColor}`, text: `${textColor}` });
        }
      }

      applyRandomColors() {
        if (!this.availableColors.length) return;
        let randomIndex;
        do {
          randomIndex = Math.floor(Math.random() * this.availableColors.length);
        } while (randomIndex === this.previousColorIndex && this.availableColors.length > 1);
        this.previousColorIndex = randomIndex;
        const { bg, text } = this.availableColors[randomIndex];
        this.parentElement.style.setProperty('--color-background-h', bg);
        this.parentElement.style.setProperty('--color-foreground-h', text);
      }

      start() {
        if (this.started) return;
        this.started = true;
        const inner = this.querySelector('.scroller__inner');
        if (!inner) return;
        const items = Array.from(inner.children);
        if (!items.length) return;
        /* duplicate the original set once so the -50% keyframe lands exactly on a copy boundary (seamless loop) */
        items.forEach(li => {
          const clone = li.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          inner.appendChild(clone);
        });
        this.inner = inner;
        this.setDuration();
        this.setAttribute('data-animated', 'true');
        this.setupPauseInterval();
        this.setupHoverEvents();
        window.addEventListener('resize', this.onResize, { passive: true });
      }

      setDuration() {
        if (!this.inner) return;
        const half = this.inner.scrollWidth / 2; /* one layout read; width of the original items */
        const duration = Math.max(1, half / this.scrollSpeed);
        this.inner.style.setProperty('--_animation-duration', duration.toFixed(2) + 's');
      }

      onResize() {
        if (this.resizeTimer) clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.setDuration(), 250);
      }

      setPlayState(state) {
        if (this.inner) this.inner.style.animationPlayState = state;
      }

      syncState() {
        const shouldRun = this.isOnScreen && !document.hidden;
        if (shouldRun && !this.started) this.start();
        if (!this.started) return;
        if (!shouldRun) this.setPlayState('paused');
        else if (!this.isHovered) this.setPlayState('running');
      }

      setupPauseInterval() {
        this.pauseInterval = setInterval(() => {
          if (this.isHovered || !this.isOnScreen || document.hidden) return;
          this.setPlayState('paused');
          this.pauseTimeout = setTimeout(() => { if (!this.isHovered) this.setPlayState('running'); }, this.pauseDuration);
        }, 7000);
      }

      onEnter() { this.isHovered = true; this.setPlayState('paused'); }
      onLeave() { this.isHovered = false; if (this.isOnScreen && !document.hidden) this.setPlayState('running'); }

      setupHoverEvents() {
        if (!this.parentElement) return;
        this.parentElement.addEventListener("mouseenter", this.onEnter);
        this.parentElement.addEventListener("mouseleave", this.onLeave);
      }
    }

    customElements.define('marquee-text', MarqueeText);
  }
})();
